
import { useCallback } from 'react';
import { useStreamingMessageHandler } from './useStreamingMessageHandler';
import { useAiServiceManagement } from './useAiServiceManagement';
import { useMessageFlow } from './useMessageFlow';
import { useUsageValidation } from './useUsageValidation';
import { MessageType, TopicType } from '@/types/assistant';

interface UseMessageOrchestrationProps {
  currentSessionId: string | null;
  userId: string | null;
  language: string;
  addUserMessage: (content: string, sessionId: string, userId: string, autoSave?: boolean) => Promise<MessageType>;
  saveMessage: (message: MessageType) => Promise<void>;
  addAiMessage: (message: MessageType, autoSave?: boolean) => Promise<void>;
  updateMessage: (messageId: string, updates: Partial<MessageType>) => void;
  startMessagePerformance: (topic: TopicType) => any;
  finishMessagePerformance: (session: any, topic: TopicType, status: 'success' | 'error', errorMessage?: string) => Promise<void>;
  recoverFromError: (error: unknown, context: string, requestId?: string) => void;
  messages: MessageType[];
  currentTopic: TopicType;
  onUsageLimitError: (error: Error, requestId: string) => void;
  onAiServiceError: (error: Error, requestId: string) => void;
  onUsageUpdate: () => void;
  setIsTyping: (typing: boolean) => void;
}

interface UseMessageOrchestrationReturn {
  handleMessageFlow: (options: any) => Promise<void>;
  validateAndIncrementUsage: (topic: TopicType, requestId: string) => Promise<any>;
  processAiRequest: (request: any) => Promise<any>;
  usageUpdateTrigger: () => void;
}

export const useMessageOrchestration = ({
  currentSessionId,
  userId,
  language,
  addUserMessage,
  addAiMessage,
  updateMessage,
  startMessagePerformance,
  finishMessagePerformance,
  recoverFromError,
  messages,
  currentTopic,
  onUsageLimitError,
  onAiServiceError,
  onUsageUpdate,
  setIsTyping
}: UseMessageOrchestrationProps): UseMessageOrchestrationReturn => {

  // Initialize streaming message handler with setIsTyping callback
  const {
    handleStreamingStart,
    handleStreamingContent,
    handleStreamingComplete,
    handleStreamingError
  } = useStreamingMessageHandler({
    addAiMessage,
    updateMessage,
    sessionId: currentSessionId,
    userId,
    onStreamingError: (error: string, requestId: string) => {
      console.error('[MESSAGE ORCHESTRATION FINAL] Streaming error:', { error, requestId });
      onAiServiceError(new Error(error), requestId);
    },
    setIsTyping
  });

  // Initialize AI service management
  const { processAiRequest } = useAiServiceManagement({
    userId,
    language,
    onAiServiceError,
    onStreamingStart: handleStreamingStart,
    onStreamingContent: handleStreamingContent,
    onStreamingComplete: handleStreamingComplete,
    onStreamingError: handleStreamingError
  });

  // Initialize usage validation hook
  const { validateAndIncrementUsage, usageUpdateTrigger: internalUsageUpdateTrigger } = useUsageValidation({
    userId,
    onUsageLimitError: (error: Error) => {
      onUsageLimitError(error, 'usage-validation');
      return true; // Indicate we handled the error
    },
    onUsageUpdate: onUsageUpdate
  });

  // Initialize message flow WITHOUT usage validation (handled at input level)
  const { handleMessageFlow } = useMessageFlow({
    currentSessionId,
    userId,
    currentTopic,
    messages,
    addUserMessage,
    addAiMessage,
    startMessagePerformance,
    finishMessagePerformance,
    recoverFromError,
    processAiRequest,
    validateAndIncrementUsage: async () => ({ allowed: true }) // Skip validation in flow
  });

  const usageUpdateTrigger = useCallback(() => {
    onUsageUpdate();
  }, [onUsageUpdate]);

  return {
    handleMessageFlow,
    validateAndIncrementUsage,
    processAiRequest,
    usageUpdateTrigger
  };
};
